<template>
  <div class="step-tab">
    <div class="cp-card">
      <p class="cp-hint">
        {{ L.pdfHint || 'The reservation-confirmation PDF sent to the seller (and to the buyer when enabled). Set the heading and intro shown at the top of the PDF, then preview it.' }}
      </p>

      <div class="cp-pdf-fields">
        <CustomInput
            :label="L.pdfTitleLabel || 'PDF title'"
            :placeholder="L.pdfTitlePlaceholder || 'Reservation Confirmation'"
            :value="text.pdf_title"
            @input="setText('pdf_title', $event)" />
        <CustomInput
            :label="L.pdfDescriptionLabel || 'PDF intro text'"
            :placeholder="L.pdfDescriptionPlaceholder || 'Thank you for your reservation.'"
            :value="text.pdf_description"
            @input="setText('pdf_description', $event)" />
      </div>

      <md-button class="md-raised md-primary cp-pdf-preview-btn" :disabled="loading" @click="preview">
        <md-icon>{{ loading ? 'hourglass_empty' : 'visibility' }}</md-icon>
        {{ loading ? (L.pdfPreviewLoading || 'Rendering…') : (L.pdfPreviewBtn || 'Preview PDF') }}
      </md-button>

      <p v-if="error" class="cp-pdf-error">{{ error }}</p>
    </div>

    <!-- Rendered preview of the actual PDF that would be sent (sample booking data). -->
    <div v-if="pdfUrl" class="cp-card cp-pdf-frame-card">
      <iframe :src="pdfUrl" class="cp-pdf-frame" title="PDF preview"></iframe>
    </div>
  </div>
</template>

<script>
import CustomInput from '../../parts/CustomInput.vue'

/**
 * PDF step tab: edits the reservation PDF's title + intro (per active language) and
 * renders a live preview by calling the backend, which builds the REAL PDF (same
 * builder used at booking) with sample data — so the preview matches what's sent.
 */
export default {
  name: 'StepTabPdf',
  components: { CustomInput },
  props: {
    options: { type: Object, required: true },
    text: { type: Object, default: () => ({}) },   // texts[lang] (mutated in place)
    lang: { type: String, default: 'de' },
    brandColor: { type: String, default: '#1a73e8' }
  },
  data() {
    return { loading: false, error: '', pdfUrl: '' };
  },
  computed: {
    L() { return this.options.Lang || {}; }
  },
  beforeDestroy() {
    if (this.pdfUrl) URL.revokeObjectURL(this.pdfUrl);
  },
  methods: {
    setText(key, val) { this.$set(this.text, key, val); },
    preview() {
      const self = this;
      this.error = '';
      this.loading = true;
      const gi = (this.options && this.options.globalinfo) || {};
      const data = {
        lang: this.lang,
        pdf_title: this.text.pdf_title || '',
        pdf_description: this.text.pdf_description || '',
        color: gi.color || this.brandColor,
        company: gi.address && gi.address.company ? gi.address.company : (gi.adresse || ''),
        logo: gi.baseLogo || ''
      };
      this.doPost({ data }, 'checkout-profiles/pdf-preview', function (res) {
        self.loading = false;
        if (res.data.status === 'error' || !res.data.data || !res.data.data.pdfBase64) {
          self.error = (self.L.pdfPreviewFailed || 'Could not render the preview.');
          return;
        }
        // base64 → Blob → object URL for the iframe.
        try {
          const byteChars = atob(res.data.data.pdfBase64);
          const bytes = new Uint8Array(byteChars.length);
          for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i);
          const blob = new Blob([bytes], { type: 'application/pdf' });
          if (self.pdfUrl) URL.revokeObjectURL(self.pdfUrl);
          self.pdfUrl = URL.createObjectURL(blob);
        } catch (e) {
          self.error = (self.L.pdfPreviewFailed || 'Could not render the preview.');
        }
      });
    }
  }
};
</script>

<style scoped>
.step-tab { animation: cp-fade .2s ease; }
@keyframes cp-fade { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }

.cp-card { background: #fff; border-radius: 1rem; box-shadow: 0 2px 12px rgba(0,0,0,0.06); padding: 1.5rem 1.75rem; }
.cp-hint { font-size: 0.8rem; color: #888; margin: 0 0 1rem; max-width: 620px; }
.cp-pdf-fields { display: flex; flex-direction: column; gap: 1rem; max-width: 560px; margin-bottom: 1.25rem; }
.cp-pdf-preview-btn .md-icon { margin-right: 0.35rem; }
.cp-pdf-error { color: #d9534f; font-size: 0.85rem; margin: 0.75rem 0 0; }

.cp-pdf-frame-card { margin-top: 1.25rem; padding: 0.75rem; }
.cp-pdf-frame { width: 100%; height: 80vh; border: none; border-radius: 0.5rem; }
</style>
